import { debounce } from 'lodash';
import React, { useState, useCallback } from 'react';

import AsyncSelect from 'react-select/async';

/** components */
import LabelWithTooltip from './LabelWithTooltip';
import SafeHtml from './SafeHtml';

import { getTenant } from '../../config';

/** helpers */
import { capitalizeFirstLetter, formatOptionName } from '../../helpers/format';
import { validateInputField } from '../../helpers/validation';

/** service */
import { getAddressSuggestions, getAddresses } from '../../service/api';

const customStyles = {
  option: (provided, state) => ({
    ...provided,
    color: '#727272',
    fontFamily: "'Lato', sans-serif !important",
    fontWeight: 300,
    fontSize: 19,
    padding: 10,
    backgroundColor: state.isFocused ? '#E0EBFD' : '#fff',
  }),
};

const BnplStyles = {
  option: (provided, state) => ({
    ...provided,
    color: '#727272',
    fontFamily: "'Lato', sans-serif !important",
    fontStyle: 'normal',
    fontWeight: '500',
    fontSize: '16px',
    lineHeight: '150%',
    padding: '9px 16px',
    backgroundColor: state.isFocused ? '#E0EBFD' : '#fff',
  }),
};

const AddressInput = ({
  field,
  data,
  setData,
  dataError,
  setDataError,
  fieldClassName,
  toggleFieldModal,
  isModalOpen,
  isShowFieldPrefix,
  initializeSectionError,
}) => {
  const [searchAddressKey, setSearchAddressKey] = useState('');
  const fieldPrefix = capitalizeFirstLetter(field?.label?.split(' ')[0]);

  const formatOptionLabel = (option) => (
    <SafeHtml
      originalHtml={formatOptionName(option.label, searchAddressKey)}
      className="selected-address"
    />
  );

  const handleNoResults = () => null;

  const [suggestionCache, setSuggestionCache] = useState({});
  const [addressCache, setAddressCache] = useState({});

  // load options using API call
  const loadOptions = async (inputValue) => {
    if (!inputValue) {
      return;
    }

    if (suggestionCache[inputValue]) {
      return suggestionCache[inputValue];
    }
    const resp = await getAddressSuggestions(inputValue);
    if (resp.status === 200) {
      const suggestions = resp.data.suggestions.map((item, index) => ({
        ...item,
        id: item.street_line,
        label: `${item.street_line}, ${item.city}, ${item.state}`,
      }));

      suggestionCache[inputValue] = suggestions;
      setSuggestionCache(suggestionCache);
      return suggestions;
    }
  };

  const debounceLoadOptions = useCallback(
    debounce((inputValue, callback) => {
      loadOptions(inputValue).then((options) => callback(options));
    }, 500),
    []
  );

  // handle input change event
  const handleInputChange = (inputValue, { action }) => {
    initializeSectionError();
    switch (action) {
      case 'input-change':
        if (!inputValue || inputValue.length < 2) {
          return '';
        }
        setSearchAddressKey(inputValue);
        return inputValue;
      default:
    }
  };

  // handle selection
  const handleChangeAddress = async (value, name, field) => {
    initializeSectionError();
    if (value) {
      let address;
      if (addressCache[value.label]) {
        address = addressCache[value.label];
      } else {
        validateInputField(value, name, field, setDataError);
        const resp = await getAddresses(value.street_line, value.city, value.state, value.zipcode);
        if (resp.status === 200 && resp.data[0]) {
          address = resp.data[0];
          addressCache[value.label] = address;
          setAddressCache(addressCache);
        }
      }
      if (address) {
        setData({
          ...data,
          [name]: {
            ...address,
            deliveryLine1: address.delivery_line_1,
            deliveryLine2: '',
            components: {
              cityName: address.components.city_name,
              state: address.components.state_abbreviation,
              zipCode: address.components.zipcode,
            },
            country: 'US',
            street_line: value.street_line,
            label: value.label,
          },
        });
      }
    } else {
      setData({ ...data, [name]: null });
    }
    setSearchAddressKey('');
  };

  const renderContent = () => {
    return (
      <>
        <div key={field.name} className={fieldClassName}>
          <div className="form-field">
            <LabelWithTooltip
              field={field}
              toggleFieldModal={toggleFieldModal}
              isModalOpen={isModalOpen}
            />
            <AsyncSelect
              defaultOptions
              name={field.name}
              value={data[field.name]}
              formatOptionLabel={formatOptionLabel}
              noOptionsMessage={handleNoResults}
              getOptionValue={(e) => e.deliveryLine1}
              loadOptions={debounceLoadOptions}
              onInputChange={handleInputChange}
              onChange={(value) => handleChangeAddress(value, field.name, field)}
              isClearable
              classNamePrefix="react-select"
              styles={getTenant() === 'lightrx' ? BnplStyles : customStyles}
            />
          </div>
          {dataError[field.name] && (
            <p className="error-text address-error">{dataError[field.name]}</p>
          )}
        </div>
        <div key={field.name + 'address2'} className="form-group address2">
          <div className="form-field">
            {getTenant() !== 'lightrx' && (
              <label htmlFor="address-2">
                {field.label.replace('Line 1', 'Line 2').replace('*', '')}
              </label>
            )}
            <input
              id="address-2"
              type="text"
              value={
                data[field.name] && Object.keys(data[field.name]).length
                  ? data[field.name]?.deliveryLine2
                  : ''
              }
              onChange={(e) =>
                setData({
                  ...data,
                  address: {
                    ...data[field.name],
                    deliveryLine2: e.target.value,
                    label: data[field.name] ? data[field.name]?.label : '',
                  },
                })
              }
              spellCheck={false}
            />
          </div>
        </div>
        <div
          key={field.name + 'city'}
          className={`form-group  ${dataError[field.name] ? 'error' : ''}`}
        >
          <div className="form-field">
            <label htmlFor="city">
              {isShowFieldPrefix ? fieldPrefix + ' City' : 'City'}
              <span className="required">{field.required ? '*' : ''}</span>
            </label>
            <input
              id="city"
              value={
                data[field.name] && Object.keys(data[field.name]).length
                  ? data[field.name]?.components?.cityName
                  : ''
              }
              type="text"
              readOnly
              spellCheck={false}
            />
          </div>
          {dataError[field.name] && <p className="error-text">{dataError[field.name]}</p>}
        </div>
        <div
          key={field.name + 'state'}
          className={`form-group mini ${dataError[field.name] ? 'error' : ''}`}
        >
          <div className="form-field">
            <label htmlFor="state">
              {isShowFieldPrefix ? fieldPrefix + ' State' : 'State'}
              <span className="required">{field.required ? '*' : ''}</span>
            </label>
            <input
              id="state"
              value={
                data[field.name] && Object.keys(data[field.name]).length
                  ? data[field.name]?.components?.state
                  : ''
              }
              type="text"
              readOnly
              spellCheck={false}
            />
          </div>
          {dataError[field.name] && <p className="error-text">{dataError[field.name]}</p>}
        </div>
        <div
          key={field.name + 'zip'}
          className={`form-group mini ${dataError[field.name] ? 'error' : ''}`}
        >
          <div className="form-field">
            <label htmlFor="zip-code">
              {isShowFieldPrefix ? fieldPrefix + ' Zip Code' : 'Zip Code'}
              <span className="required">{field.required ? '*' : ''}</span>
            </label>
            <input
              id="zip-code"
              value={
                data[field.name] && Object.keys(data[field.name]).length
                  ? data[field.name]?.components?.zipCode
                  : ''
              }
              type="text"
              readOnly
              spellCheck={false}
            />
          </div>
          {dataError[field.name] && <p className="error-text">{dataError[field.name]}</p>}
        </div>
      </>
    );
  };

  return (
    <>
      {getTenant() === 'lightrx' ? (
        <div className="lightrx-address-input">{renderContent()}</div>
      ) : (
        renderContent()
      )}
    </>
  );
};

export default AddressInput;
